import { useMemo } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  IcmAnimatedMoney,
  IcmMoneyDisplay,
  splitFormattedMoney,
  IcmTabbedPieChart,
} from "@/icm/common";
import type { EnrichedIcmStatement, IcmJob } from "@/icm/types";
import { buildJobsPieChartTabs } from "@/icm/utils";
import { getSummaryMetrics } from "./utils";

interface IcmPageSummaryCardProps {
  statements: EnrichedIcmStatement[];
  jobs: IcmJob[];
}

export const IcmPageSummaryCard = ({
  statements,
  jobs,
}: IcmPageSummaryCardProps) => {
  const {
    totalPayout,
    totalRevenue,
    totalRevenueFormatted,
    commissionRate,
    uniqueJobCount,
  } = useMemo(() => getSummaryMetrics(statements, jobs), [statements, jobs]);
  const pieChartTabs = useMemo(() => buildJobsPieChartTabs(jobs), [jobs]);
  const revenue = splitFormattedMoney(totalRevenueFormatted);
  const hasJobs = jobs.length > 0;

  return (
    <Card className="w-full gap-0 p-6 py-0 [@media(max-width:1275px)]:w-fit">
      <Stack
        gap={4}
        direction="row"
        justify="between"
        align="center"
        className="w-full [@media(max-width:1275px)]:flex-col [@media(max-width:1275px)]:items-start"
      >
        <Stack direction="row" align="center" gap={8} className="flex-wrap">
          <Stack align="start" gap={1} className="px-6">
            <Text variant="label" tone="muted">
              Total pay
            </Text>
            <IcmAnimatedMoney
              value={totalPayout}
              className="text-3xl leading-[1.1] font-medium tracking-[-0.02em] text-brand"
              centsClassName="text-base font-normal text-subtle"
            />
          </Stack>
          <Separator orientation="vertical" className="h-20" />

          <Stack align="start" gap={4}>
            <Stack align="start" gap={1}>
              <Text variant="label" tone="muted">
                Technicians
              </Text>
              <Text variant="display" tone="muted">
                {statements.length}
              </Text>
            </Stack>
            {uniqueJobCount > 0 && (
              <Stack align="start" gap={1}>
                <Text variant="label" tone="muted">
                  Jobs worked
                </Text>
                <Text variant="display" tone="muted">
                  {uniqueJobCount}
                </Text>
              </Stack>
            )}
          </Stack>

          <Separator orientation="vertical" className="h-20" />

          <Stack align="start" gap={4}>
            {totalRevenue > 0 && (
              <Stack align="start" gap={1}>
                <Text variant="label" tone="muted">
                  Total Revenue
                </Text>
                <IcmMoneyDisplay
                  dollars={revenue.dollars}
                  cents={revenue.cents}
                  className="text-2xl leading-[1.1] font-medium tracking-[-0.02em] text-subtle"
                  centsClassName="pt-0.5 text-xs font-normal text-subtle"
                />
              </Stack>
            )}

            {commissionRate !== null && (
              <Stack align="start" gap={1}>
                <Text variant="label" tone="muted">
                  Pay % of Revenue
                </Text>
                <Text variant="display" tone="muted">
                  {commissionRate.toFixed(1)}%
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>
        {hasJobs && (
          <Stack align="start" gap={4} className="px-6">
            <IcmTabbedPieChart tabs={pieChartTabs} />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
